import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import {
  buildExportExcelPanelTimeRange,
  generateDefaultGridSize,
  getExportExcelPanelSamplesMode,
} from '@/tools/exportExcel/exportExcelPanel.utilities';
import { GRID_OPTION, SAMPLES_TABLE_MODE } from '@/tools/exportOData/odata.constants';
import {
  getNextToolParameterUnsetValue,
  getNextToolParameterValue,
  getRequiredToolParameterDefinition,
} from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { Moment } from 'moment';
import type { Timezone } from '@/datetime/timezone.service';
import type { SamplesTableMode } from '@/tools/exportExcel/exportExcelPanel.types';
import type { ParameterDefinitions, ToolItem } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type GridOptionValue = (typeof GRID_OPTION)[keyof typeof GRID_OPTION];

export interface ExportODataPanelTimeRange {
  start: number;
  end: number;
}

export interface ExportODataPanelState {
  /** ID of the OData export being edited, or `undefined` when creating a new export. */
  id: string | undefined;
  /** Name of the export, defaults to the workbook/worksheet name. */
  name: string | undefined;
  /** Suffix of a saved export's name (after the export separator), preserved when the export is updated. */
  exportNameSuffix: string;
  /** Whether the export's time range automatically moves forward with time. */
  autoupdateTimeRange: boolean;
  /** Time range for the export, in epoch milliseconds. */
  timeRange: ExportODataPanelTimeRange;
  /** Time zone the export is formatted in; falls back to the worksheet time zone when unset. */
  exportTimeZone: Timezone | undefined;
  /** Which grid-sizing strategy to use for the exported samples. */
  gridOption: GridOptionValue;
  /** Grid size used when gridOption is CUSTOM. */
  gridSize: ValueWithUnitsItem;
  /** Origin used for gridding, in epoch milliseconds. */
  gridOrigin: number;
  /** Whether gridOrigin should be applied when gridOption is CUSTOM. */
  gridOriginEnabled: boolean;
  /** Whether the samples table should be rendered in chain-view mode. */
  chainView: boolean;
  /** Whether the samples table should be rendered in capsule-time mode. */
  capsuleTime: boolean;
  /** Whether a saved export includes capsules; `undefined` for new exports. */
  exportCapsules: boolean | undefined;
  /** Link to the worksheet a saved export was created from. */
  worksheetLink: string;
  /** Signals selected for export, populated from the trend selection when the tool is opened. */
  exportSignals: ToolItem[] | undefined;
  /** Conditions selected for export, populated from the trend selection when the tool is opened. */
  exportConditions: ToolItem[] | undefined;
  /** Whether free text context columns are included in the export. */
  includeTextContext: boolean;
}

/** State applied when an existing export is loaded for editing. The time range bounds are optional. */
export type ExportODataEditState = Omit<ExportODataPanelState, 'timeRange' | 'worksheetLink'> & {
  timeRange: Partial<ExportODataPanelTimeRange>;
};

export interface ExportODataPanelActions {
  setSearchName: (name: string) => void;
  setODataId: (id: string) => void;
  setODataWorksheetLink: (worksheetLink: string) => void;
  setAutoupdateTimeRange: (autoupdateTimeRange: boolean) => void;
  setTimeRangeStart: (start: Moment | Date) => void;
  setTimeRangeEnd: (end: Moment | Date) => void;
  /** Shifts the time range start by the given duration in milliseconds, keeping end fixed. */
  setDuration: (durationMs: number) => void;
  setExportTimeZone: (exportTimeZone: Timezone) => void;
  /**
   * Sets the grid option and resets the grid size to the given default. Disables the grid origin unless the option
   * is CUSTOM.
   */
  setGridOption: (gridOption: GridOptionValue, defaultGridSize: ValueWithUnitsItem) => void;
  setGridSize: (gridSize: ValueWithUnitsItem) => void;
  setGridOrigin: (gridOrigin: Moment | Date) => void;
  setGridOriginEnabled: (gridOriginEnabled: boolean) => void;
  /** Sets chainView/capsuleTime together from the selected samples table mode. */
  setSamplesMode: (samplesMode: SamplesTableMode) => void;
  setIncludeTextContext: (includeTextContext: boolean) => void;
  setExportSignalItem: (item: ToolItem | null | undefined) => void;
  unsetExportSignalItem: (item: ToolItem | null | undefined) => void;
  setExportConditionItem: (item: ToolItem | null | undefined) => void;
  unsetExportConditionItem: (item: ToolItem | null | undefined) => void;
  /** Populates the form from a saved export so it can be edited. */
  setODataExportForEdit: (editState: ExportODataEditState) => void;
  reset: (overrides?: Partial<ExportODataPanelState>) => void;
}

export type ExportODataDehydratedState = ExportODataPanelState;

const exportItemParameterDefinitions: ParameterDefinitions<'exportSignals' | 'exportConditions'> = {
  exportSignals: { predicate: ['name', 'a'], multiple: true },
  exportConditions: { predicate: ['name', 'a'], multiple: true },
};

const initialState: ExportODataPanelState = Object.freeze({
  id: undefined,
  name: undefined,
  exportNameSuffix: '',
  autoupdateTimeRange: false,
  timeRange: { start: 0, end: 0 },
  exportTimeZone: undefined,
  gridOption: GRID_OPTION.CUSTOM,
  gridSize: generateDefaultGridSize(0),
  gridOrigin: 0,
  gridOriginEnabled: false,
  chainView: false,
  capsuleTime: false,
  exportCapsules: undefined,
  worksheetLink: '',
  exportSignals: undefined,
  exportConditions: undefined,
  includeTextContext: false,
});

export const dehydrateExportODataPanelState = (state: ExportODataPanelState): ExportODataDehydratedState => state;

const exportODataPanelActionsFactory: ActionsFactory<ExportODataPanelState, ExportODataPanelActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setODataId: (id) =>
    set((draft) => {
      draft.id = id;
    }),

  setODataWorksheetLink: (worksheetLink) =>
    set((draft) => {
      draft.worksheetLink = worksheetLink;
    }),

  setAutoupdateTimeRange: (autoupdateTimeRange) =>
    set((draft) => {
      draft.autoupdateTimeRange = autoupdateTimeRange;
    }),

  setTimeRangeStart: (start) =>
    set((draft) => {
      draft.timeRange.start = start.valueOf();
    }),

  setTimeRangeEnd: (end) =>
    set((draft) => {
      draft.timeRange.end = end.valueOf();
    }),

  setDuration: (durationMs) =>
    set((draft) => {
      draft.timeRange.start = draft.timeRange.end - durationMs;
    }),

  setExportTimeZone: (exportTimeZone) =>
    set((draft) => {
      draft.exportTimeZone = exportTimeZone;
    }),

  setGridOption: (gridOption, defaultGridSize) =>
    set((draft) => {
      draft.gridOption = gridOption;
      if (gridOption !== GRID_OPTION.CUSTOM) {
        draft.gridOriginEnabled = false;
      }
      draft.gridSize = defaultGridSize;
    }),

  setGridSize: (gridSize) =>
    set((draft) => {
      draft.gridSize = gridSize;
    }),

  setGridOrigin: (gridOrigin) =>
    set((draft) => {
      draft.gridOrigin = gridOrigin.valueOf();
    }),

  setGridOriginEnabled: (gridOriginEnabled) =>
    set((draft) => {
      draft.gridOriginEnabled = gridOriginEnabled;
    }),

  setSamplesMode: (samplesMode) =>
    set((draft) => {
      draft.chainView = samplesMode === SAMPLES_TABLE_MODE.CHAIN;
      draft.capsuleTime = samplesMode === SAMPLES_TABLE_MODE.CAPSULE;
    }),

  setIncludeTextContext: (includeTextContext) =>
    set((draft) => {
      draft.includeTextContext = includeTextContext;
    }),

  setExportSignalItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportSignals');
      const nextValue = getNextToolParameterValue({ definition, currentValue: draft.exportSignals, item });
      if (nextValue.changed) {
        draft.exportSignals = nextValue.value as ToolItem[];
      }
    }),

  unsetExportSignalItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportSignals');
      const nextValue = getNextToolParameterUnsetValue({ definition, currentValue: draft.exportSignals, item });
      if (nextValue.changed) {
        draft.exportSignals = nextValue.value as ToolItem[];
      }
    }),

  setExportConditionItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportConditions');
      const nextValue = getNextToolParameterValue({ definition, currentValue: draft.exportConditions, item });
      if (nextValue.changed) {
        draft.exportConditions = nextValue.value as ToolItem[];
      }
    }),

  unsetExportConditionItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportConditions');
      const nextValue = getNextToolParameterUnsetValue({ definition, currentValue: draft.exportConditions, item });
      if (nextValue.changed) {
        draft.exportConditions = nextValue.value as ToolItem[];
      }
    }),

  setODataExportForEdit: ({ timeRange, ...editState }) =>
    set((draft) => {
      Object.assign(draft, editState);
      if (timeRange.start !== undefined) {
        draft.timeRange.start = timeRange.start;
      }
      if (timeRange.end !== undefined) {
        draft.timeRange.end = timeRange.end;
      }
    }),

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'ExportODataPanel';

const [ExportODataPanelStoreProvider, useExportODataPanelStoreInstance, useExportODataPanelSelector] = createStore(
  STORE_NAME,
  initialState,
  exportODataPanelActionsFactory,
);

export { ExportODataPanelStoreProvider };

export const useExportODataPanelId = () => useExportODataPanelSelector((state) => state.id);
export const useExportODataPanelName = () => useExportODataPanelSelector((state) => state.name);
export const useExportODataPanelExportNameSuffix = () => useExportODataPanelSelector((state) => state.exportNameSuffix);
export const useExportODataPanelAutoupdateTimeRange = () =>
  useExportODataPanelSelector((state) => state.autoupdateTimeRange);
export const useExportODataPanelExportTimeZone = () => useExportODataPanelSelector((state) => state.exportTimeZone);
export const useExportODataPanelGridOption = () => useExportODataPanelSelector((state) => state.gridOption);
export const useExportODataPanelGridSize = () => useExportODataPanelSelector((state) => state.gridSize);
export const useExportODataPanelGridOrigin = () => useExportODataPanelSelector((state) => state.gridOrigin);
export const useExportODataPanelGridOriginEnabled = () =>
  useExportODataPanelSelector((state) => state.gridOriginEnabled);
export const useExportODataPanelChainView = () => useExportODataPanelSelector((state) => state.chainView);
export const useExportODataPanelCapsuleTime = () => useExportODataPanelSelector((state) => state.capsuleTime);
export const useExportODataPanelExportCapsules = () => useExportODataPanelSelector((state) => state.exportCapsules);
export const useExportODataPanelWorksheetLink = () => useExportODataPanelSelector((state) => state.worksheetLink);
export const useExportODataPanelExportSignals = () => useExportODataPanelSelector((state) => state.exportSignals);
export const useExportODataPanelExportConditions = () => useExportODataPanelSelector((state) => state.exportConditions);
export const useExportODataPanelIncludeTextContext = () =>
  useExportODataPanelSelector((state) => state.includeTextContext);

const selectTimeRange = createSelector(
  (state: ExportODataPanelState) => state.timeRange,
  buildExportExcelPanelTimeRange,
);
/** The export time range, converted from raw epoch milliseconds into a moment-based range. */
export const useExportODataPanelTimeRange = () => useExportODataPanelSelector(selectTimeRange);

const selectSamplesMode = createSelector(
  (state: ExportODataPanelState) => state.chainView,
  (state: ExportODataPanelState) => state.capsuleTime,
  getExportExcelPanelSamplesMode,
);
export const useExportODataPanelSamplesMode = () => useExportODataPanelSelector(selectSamplesMode);

export const useExportODataPanelActions = () => useExportODataPanelStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateExportODataPanelStore() {
  return createStoreInstance<ExportODataPanelState, ExportODataPanelActions>(
    initialState,
    exportODataPanelActionsFactory,
    {
      storeName: STORE_NAME,
    },
  );
}

export const usePersistExportODataPanelStore = () => {
  const register = useRegisterPersistedStore();
  const store = useExportODataPanelStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqExportODataPanelStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateExportODataPanelState,
    });
  }, [register, store]);
};
